import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Maximize2, RotateCcw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { SoundStylePicker } from "../SoundStylePicker";
import { burst } from "../../lib/fx";
import { sfx } from "../../lib/sound";
import { SoundToggle, useSound } from "../SoundToggle";
import { MASCOT_CHEER } from "../../lib/tones";
import { PrizeWheel } from "../reveal/PrizeWheel";
import { resultSize } from "../reveal/ResultReveal";
import { labelOf, type GroupState } from "./GroupParts";

const SPIN_SECONDS = 2.2;

interface Props { group: GroupState; open: boolean; onClose: () => void; fast: boolean; hostToken?: string; onChanged?: () => void }

export function GroupWheelReveal({ group, open, onClose, fast, hostToken, onChanged }: Props) {
  const [landed, setLanded] = useState(false);
  const [run, setRun] = useState(0);
  const style = group.sound_style;
  const setStyle = async (s: string) => {
    try {
      await api.post(`/groups/${group.id}/sound-style`, { style: s }, { headers: { "X-Host-Token": hostToken } });
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const sound = useSound();
  const winner = labelOf(group, group.winner_option_id);
  const last = group.rounds[group.rounds.length - 1];
  const tied = group.final_tiebreak_ids.length ? group.final_tiebreak_ids : last?.dither_tiebreak ? last.tied_option_ids : [];

  useEffect(() => {
    if (!open) return;
    setLanded(false);
    if (sound && !fast) {
      sfx.spin(SPIN_SECONDS, 34, style);
      sfx.drumroll(SPIN_SECONDS, style);
    }
    const t = setTimeout(() => {
      setLanded(true);
      if (!fast) burst();
      if (sound) sfx.fanfare(style);
    }, fast ? 0 : SPIN_SECONDS * 1000 + 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fast, run]);

  useEffect(() => {
    if (!open && document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
  }, [open]);

  if (!open) return null;
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())?.catch?.(() => undefined);

  return (
    <div className="rays fixed inset-0 z-[75] flex flex-col items-center justify-center overflow-y-auto px-4 py-16" data-testid="group-wheel-reveal">
      <div className="fixed right-4 top-4 flex items-center gap-2">
        <SoundToggle place="wheel-reveal" />
        <button onClick={() => setRun((r) => r + 1)} aria-label="Replay reveal" className="btn-pop rounded-full bg-white p-2" data-testid="group-wheel-replay-button"><RotateCcw className="h-5 w-5" strokeWidth={3} /></button>
        <button onClick={fullscreen} aria-label="Full screen" className="btn-pop rounded-full bg-white p-2" data-testid="group-wheel-fullscreen-button"><Maximize2 className="h-5 w-5" strokeWidth={3} /></button>
        <button onClick={onClose} aria-label="Close" className="btn-pop rounded-full bg-white p-2" data-testid="group-wheel-reveal-close"><X className="h-5 w-5" strokeWidth={3} /></button>
      </div>
      <p className="mb-6 max-w-2xl text-center font-display text-2xl font-bold uppercase sm:text-4xl" data-testid="group-wheel-title">{group.title}</p>
      {!landed ? (
        <>
          <PrizeWheel labels={group.options.map((o) => o.label)} result={winner} fast={fast} duration={SPIN_SECONDS}
            className="h-72 w-72 sm:h-[26rem] sm:w-[26rem] lg:h-[32rem] lg:w-[32rem]" />
          <p className="mt-6 font-display text-2xl font-bold uppercase animate-wiggle">Drumroll…</p>
        </>
      ) : (
        <motion.div initial={fast ? false : { scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 420, damping: 16 }}
          className="flex w-full max-w-3xl flex-col items-center rounded-[34px] border-[4px] border-ink bg-white px-6 py-10 text-center pop-shadow-lg">
          <img src={MASCOT_CHEER} alt="DITHER mascot cheering" className="animate-bob h-32 w-32 rounded-full border-[4px] border-ink object-cover sm:h-44 sm:w-44" data-testid="group-wheel-mascot" />
          <span className="mt-5 -rotate-2 rounded-full border-[3px] border-ink bg-pop-pink px-5 py-1 font-display text-xl font-bold uppercase text-white">The group says</span>
          <h2 className={`comic-title mt-4 text-pop-orange [overflow-wrap:anywhere] ${resultSize(winner)} lg:text-8xl`} data-testid="group-wheel-winner">{winner}</h2>
          {tied.length > 1 && (
            <p className="mt-4 inline-flex items-center gap-1 font-bold text-pop-purple"><Sparkles className="h-4 w-4" />DITHER broke a tie between {tied.map((t) => labelOf(group, t)).join(", ")}</p>
          )}
        </motion.div>
      )}
      {hostToken && (
        <div className="mt-8 rounded-2xl border-[3px] border-ink bg-white/90 p-3 backdrop-blur" data-testid="group-wheel-style-panel">
          <p className="mb-2 text-center text-[11px] font-extrabold uppercase tracking-widest text-zinc-500">Host · reveal sound style</p>
          <SoundStylePicker value={style} onChange={setStyle} place="reveal" compact />
        </div>
      )}
    </div>
  );
}
